import { For, type JSX, Show } from "solid-js";
import { cn } from "@space/shared";

/**
 * Barra de pestañas "píldora" — fuente ÚNICA.
 *
 * El mismo bloque de ~20 líneas estaba copiado literalmente en el detalle de
 * cohorte, el expediente del alumno y el directorio de personas (mismas clases,
 * mismo `role="tablist"`, mismo patrón de `data-testid`). Aquí vive una sola
 * vez; el marcado y los `data-testid` son idénticos a los originales para no
 * mover los e2e.
 *
 * Movimiento (contención deliberada):
 *   - Realimentación al pulsar: `active:scale-[0.97]` sobre el disparador. Solo
 *     `transform`, ~120 ms.
 *   - Entrada del panel: {@link PillTabPanels} anima SOLO la ENTRADA (200 ms,
 *     `opacity` + `transform`). No hay animación de salida a propósito: estas
 *     son pantallas de trabajo (asistencia, notas, roster) y un `out-in`
 *     duplicaría la latencia percibida del cambio de pestaña (~400 ms) sin
 *     aportar información. El contenido nuevo está en el DOM y es pulsable
 *     desde el primer frame.
 *   - `prefers-reduced-motion` lo resuelve el guard global de `global.css`.
 */

/** `id` del disparador de una pestaña; también sirve de `aria-labelledby`. */
function pillTabId(prefix: string, tabId: string): string {
  return `${prefix}-${tabId}`;
}

/** `id` del panel asociado a una barra de pestañas. */
function pillTabPanelId(prefix: string): string {
  return `${prefix}-panel`;
}

export interface PillTab<T extends string> {
  id: T;
  /** Etiqueta ya traducida por el llamante. */
  label: string;
}

interface PillTabsProps<T extends string> {
  tabs: readonly PillTab<T>[];
  value: T;
  onChange: (id: T) => void;
  /**
   * Prefijo de `data-testid` e `id`: cada disparador queda como
   * `${testIdPrefix}-${tab.id}`.
   */
  testIdPrefix: string;
  /** Etiqueta accesible de la barra (`aria-label` del `tablist`). */
  label: string;
  /** Contenido opcional a la derecha de la etiqueta (p. ej. un contador). */
  trailing?: (id: T) => JSX.Element;
  class?: string;
}

export function PillTabs<T extends string>(props: PillTabsProps<T>) {
  return (
    <div
      class={cn("flex gap-1 overflow-x-auto rounded-xl bg-muted/40 p-1", props.class)}
      role="tablist"
      aria-label={props.label}
    >
      <For each={props.tabs}>
        {(tabDef) => {
          const isSelected = () => props.value === tabDef.id;
          return (
            <button
              type="button"
              role="tab"
              id={pillTabId(props.testIdPrefix, tabDef.id)}
              data-testid={pillTabId(props.testIdPrefix, tabDef.id)}
              aria-selected={isSelected()}
              aria-controls={pillTabPanelId(props.testIdPrefix)}
              onClick={() => {
                props.onChange(tabDef.id);
              }}
              class={cn(
                "flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold",
                // Solo `color`/`background-color` en reposo y `transform` al
                // pulsar: ninguna de las dos fuerza layout.
                "transition-[color,background-color,transform] duration-150 ease-out active:scale-[0.97]",
                isSelected()
                  ? "bg-surface shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {tabDef.label}
              {props.trailing?.(tabDef.id)}
            </button>
          );
        }}
      </For>
    </div>
  );
}

interface PillTabPanelsProps {
  /** Pestaña activa. Cambiarla remonta el panel y dispara la entrada. */
  value: string;
  /** Mismo prefijo que se pasó a {@link PillTabs}. */
  testIdPrefix: string;
  children: JSX.Element;
  class?: string;
}

/**
 * Contenedor del panel activo. Al cambiar `value` el `<Show keyed>` remonta el
 * envoltorio y el `@keyframes list-entrance` se reproduce una vez.
 */
export function PillTabPanels(props: PillTabPanelsProps) {
  return (
    <Show when={props.value} keyed>
      {(key) => (
        <div
          id={pillTabPanelId(props.testIdPrefix)}
          role="tabpanel"
          aria-labelledby={pillTabId(props.testIdPrefix, key)}
          data-tab-panel={key}
          class={cn("animate-list-entrance", props.class)}
        >
          {props.children}
        </div>
      )}
    </Show>
  );
}
